import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Camera,
    Film,
    Image as ImageIcon,
    Palette,
    Building2,
    Factory,
    Cpu,
    Package,
    ShoppingBag,
    ShieldCheck,
    Layers,
    Lightbulb
} from 'lucide-react';

const commercialServices = [
    { id: '01', icon: ShoppingBag, title: 'E-Commerce & Product Media', desc: 'Crisp, color-accurate, and high-definition catalog assets optimized for global marketplaces.' },
    { id: '02', icon: Package, title: 'Hero Advertising Shoots', desc: 'High-concept creative setups engineered to define print advertisements and digital billboards.' },
    { id: '03', icon: Building2, title: 'Corporate & Architectural', desc: 'Capturing state-of-the-art office infrastructure, flagship layouts, and corporate workspaces.' },
    { id: '04', icon: Lightbulb, title: 'Creative Brand Ideation', desc: 'Custom mood-boarding, set staging, and commercial lighting configurations tailored to your brand book.' },
    { id: '05', icon: Layers, title: 'High-Fidelity Retouching', desc: 'Flawless digital compositing, color correction, and product surface texture cleanup.' },
    { id: '06', icon: Film, title: 'Commercial Sizzle Reels', desc: 'Polished micro-video campaigns for social media advertising and product launch rollouts.' },
];

const industrialServices = [
    { id: '01', icon: Factory, title: 'Heavy Industry & Manufacturing', desc: 'Documenting assembly lines, mega-structures, and massive mechanical operations in action.' },
    { id: '02', icon: Cpu, title: 'Tech, Cleanrooms & R&D', desc: 'Precise visual capture of delicate laboratories, micro-tech production, and clinical data spaces.' },
    { id: '03', icon: ShieldCheck, title: 'Safety & Compliance Portfolios', desc: 'Visual documentation explicitly framed around highlighting operational safety and workspace compliance.' },
    { id: '04', icon: Building2, title: 'Infrastructure & Logistics', desc: 'Dynamic, wide-angle captures of large-scale supply chain fulfillment networks and shipyards.' },
    { id: '05', icon: Film, title: 'Corporate Capability Films', desc: 'Documentary-grade industrial video assets showcasing your plant floor capabilities to global stakeholders.' },
];

const completeB2BOfferings = [
    { id: '01', icon: Camera, title: 'Macro Product Detail Sets', desc: 'Ultra-magnified detail captures emphasizing component engineering and build textures.' },
    { id: '02', icon: Palette, title: 'Brand Guideline Matching', desc: 'Strict calibration of lighting and backgrounds to seamlessly align with your enterprise design system.' },
    { id: '03', icon: ShieldCheck, title: 'PPE-Compliant Site Teams', desc: 'Our onsite crews arrive fully equipped with certified safety gear and strict protocol compliance training.' },
    { id: '04', icon: Factory, title: 'Plant-Floor Action Tracking', desc: 'Candid, non-disruptive capturing of working machinery and skilled technician procedures.' },
    { id: '05', icon: ImageIcon, title: 'Industrial Grade HDR Stills', desc: 'Advanced high-dynamic-range imaging configured to handle challenging, high-contrast factory light environments.' },
    { id: '06', icon: Building2, title: 'Real Estate & Facade Media', desc: 'Stunning exterior and interior imagery optimized for structural architectural engineering portfolios.' },
    { id: '07', icon: Film, title: 'Explainer & Training Media', desc: 'Step-by-step visual documentation breaking down complex apparatus usage and technical workflows.' },
    { id: '08', icon: Layers, title: 'CGI & CAD Hybrid Integrations', desc: 'Aligning studio photography tracks cleanly with raw architectural 3D blueprints and design CAD files.' },
    { id: '09', icon: ImageIcon, title: 'Corporate Investor Reports', desc: 'Editorial-grade annual report assets displaying raw corporate capability, growth, and development.' },
];

const CommercialAndIndustrial = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.6;
        const speedRight = 0.4;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>

            <Helmet>
                <title>Commercial & Industrial Photography in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Professional Commercial & Industrial Photography in Hyderabad by KhultStudios. We specialize in factory photography, manufacturing plants, corporate branding, machinery, infrastructure, architecture, warehouses, and industrial videography."
                />

                <meta
                    name="keywords"
                    content="Commercial Photography Hyderabad, Industrial Photography Hyderabad, Factory Photography, Manufacturing Photography, Corporate Photography Hyderabad, Industrial Videography, Machinery Photography, Infrastructure Photography, Plant Photography, Warehouse Photography, Architecture Photography, Business Photography Hyderabad, Brand Photography, Corporate Branding Photography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/commercial-and-industrial-photography"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/commercial-and-industrial-photography"
                />
                <meta
                    property="og:title"
                    content="Commercial & Industrial Photography in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Showcase your business with professional commercial and industrial photography. Factory, manufacturing, corporate, machinery, architecture, and industrial videography services in Hyderabad."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/commercial-industrial-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Commercial & Industrial Photography | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Professional commercial, factory, manufacturing, corporate, and industrial photography services in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/commercial-industrial-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/khultstudio.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Commercial & Industrial Media Production
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Translating Enterprise Power into Visual Assets
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Your operational capabilities and physical assets represent your brand's true scale. At KhultStudio, we convert complex industrial environments, corporate architecture, and premium consumer goods into visually arresting commercial portfolios and dynamic capability corporate films.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Deploying high-performance HDR technology, studio color calibration, and a deep understanding of manufacturing workflows, we create market-facing imagery designed to pitch directly to your future enterprise clients.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Solutions
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-linkedin-in" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-youtube" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=500" alt="Industrial Manufacturing Machine" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1540555700478-4be289fbecef?q=80&w=500" alt="Studio Product Commercial" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?q=80&w=500" alt="Construction Infrastructure Set" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=500" alt="Corporate Architecture Exterior" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=500" alt="Industrial Manufacturing Machine Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1540555700478-4be289fbecef?q=80&w=500" alt="Studio Product Commercial Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?q=80&w=500" alt="Construction Infrastructure Set Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=500" alt="Corporate Architecture Exterior Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/commercial-and-industrial/commercial-and-industrial-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/commercial-and-industrial/commercial-and-industrial-photography2.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/commercial-and-industrial/commercial-and-industrial-photography3.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/commercial-and-industrial/commercial-and-industrial-photography4.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/commercial-and-industrial/commercial-and-industrial-photography5.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/commercial-and-industrial/commercial-and-industrial-photography6.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/commercial-and-industrial/commercial-and-industrial-photography7.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/commercial-and-industrial/commercial-and-industrial-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* COMMERCIAL PHOTOGRAPHY SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">📦 Premium Commercial & Product Brand Assets</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        In highly saturated consumer markets, visuals drive conversions. Our commercial studio builds hyper-detailed, clean, and color-calibrated assets perfect for enterprise e-commerce platforms and global advertising campaigns.
                    </p>
                    <div className="row g-4 mt-2">
                        {commercialServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 1 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INDUSTRIAL PHOTOGRAPHY SERVICES SECTION */}
            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">⚙️ Enterprise Industrial & Infrastructure Photography</h2>
                        <p className="text-light-gray mt-3 max-w-2xl mx-auto">
                            Documenting heavy industries requires specialized precision. We capture large-scale automated machinery, assembly plants, and high-tech cleanroom operations while maintaining total alignment with corporate safety protocols.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {industrialServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* B2B OPERATIONAL COMPREHENSIVE MATRIX */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">💼 Complete B2B Media Deliverables</h2>
                    <div className="row g-4">
                        {completeB2BOfferings.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CommercialAndIndustrial
